import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { PlusIcon, TrashIcon, EyeIcon, CodeBracketIcon } from "@heroicons/react/24/outline"
import NewsletterAPI from "../../api"
import TemplateVariablesModal from "../components/TemplateVariablesModal"
import PagePreview from "aldoo-ra/HtmlEditor/plugins/page-preview"
import { api } from "aldoo-ra/API"

const TemplatesStep = ({ formData, errors, onInputChange, clearFieldError, releaseId }) => {
  const [availableTemplates, setAvailableTemplates] = useState([])
  const [loadingTemplates, setLoadingTemplates] = useState(true)
  const [showVariablesModal, setShowVariablesModal] = useState(false)
  const [selectedTemplateForVariables, setSelectedTemplateForVariables] = useState(null)

  // Debug logging for errors

  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true)
      const response = await NewsletterAPI.getTemplates()
      if (response.success) {
        setAvailableTemplates(response.templates || [])
      }
    } catch (error) {
      console.error('Failed to load templates:', error)
    } finally {
      setLoadingTemplates(false)
    }
  }

  const addTemplate = () => {
    const newTemplate = {
      templateId: "",
      templateName: "",
      percentage: 0
    }
    const updatedTemplates = [...formData.templates, newTemplate]

    // Auto-distribute percentages
    const equalPercentage = Math.floor(100 / updatedTemplates.length)
    const remainder = 100 - (equalPercentage * updatedTemplates.length)

    const rebalancedTemplates = updatedTemplates.map((template, index) => ({
      ...template,
      percentage: equalPercentage + (index < remainder ? 1 : 0)
    }))

    onInputChange("templates", rebalancedTemplates)
  }

  const removeTemplate = (index) => {
    const updatedTemplates = formData.templates.filter((_, i) => i !== index)

    if (updatedTemplates.length === 0) {
      onInputChange("templates", updatedTemplates)
      return
    }

    // Auto-distribute percentages
    const equalPercentage = Math.floor(100 / updatedTemplates.length)
    const remainder = 100 - (equalPercentage * updatedTemplates.length)

    const rebalancedTemplates = updatedTemplates.map((template, idx) => ({
      ...template,
      percentage: equalPercentage + (idx < remainder ? 1 : 0)
    }))

    onInputChange("templates", rebalancedTemplates)
  }

  const updateTemplate = (index, field, value) => {
    const updatedTemplates = [...formData.templates]
    updatedTemplates[index] = {
      ...updatedTemplates[index],
      [field]: value
    }
    
    // Auto-update template name when template is selected
    if (field === "templateId") {
      const selectedTemplate = availableTemplates.find(t => t._id === value)
      if (selectedTemplate) {
        updatedTemplates[index].templateName = selectedTemplate.name || "Unknown Template"
      }
      
      // Clear template ID error when user selects a template
      if (value && clearFieldError) {
        clearFieldError(`template_${index}_id`)
      }
    }
    
    // Clear percentage error when user changes percentage
    if (field === "percentage" && clearFieldError) {
      clearFieldError(`template_${index}_percentage`)
    }
    
    onInputChange("templates", updatedTemplates)
  }

  const getTotalPercentage = () => {
    return formData.templates.reduce((sum, template) => sum + (template.percentage || 0), 0)
  }

  const autoDistributePercentages = () => {
    if (formData.templates.length === 0) return
    
    const equalPercentage = Math.floor(100 / formData.templates.length)
    const remainder = 100 - (equalPercentage * formData.templates.length)
    
    const updatedTemplates = formData.templates.map((template, index) => ({
      ...template,
      percentage: equalPercentage + (index < remainder ? 1 : 0)
    }))
    
    onInputChange("templates", updatedTemplates)
  }

  const getTemplateOptions = (currentTemplateId = null) => {
    return availableTemplates
      .filter(template => {
        // Hide templates that are already selected in other rows
        const isSelectedElsewhere = formData.templates.some(t => 
          t.templateId === template._id && template._id !== currentTemplateId
        )
        return !isSelectedElsewhere
      })
      .map(template => ({
        value: template._id,
        label: template.name || "Untitled Template"
      }))
  }

  const handleEditVariables = (template) => {
    setSelectedTemplateForVariables(template)
    setShowVariablesModal(true)
  }

  const handleVariablesSaved = () => {
    // Variables have been saved, we can refresh or show success
  }

  const handleTemplatePreview = async (templateId) => {
    try {
      // Use CRUD API to fetch the template content from CMSContent
      const crudAPI = api('crud')
      const response = await crudAPI({
        get: {
          model: 'CMSContent',
          match: { _id: templateId }
        }
      })
      
      if (response?.length > 0) {
        const template = response[0]
        
        // Use the blog editor preview plugin
        PagePreview({
          action: "toggle",
          data: {
            getContent: () => {
              // Get the HTML source from the CMS template
              const templateSource = template.content?.source
              
              if (templateSource) {
                // Replace template variables with sample data for preview
                let previewHtml = templateSource
                const sampleData = template.content?.data || {}
                
                // Replace template variables with actual data
                Object.keys(sampleData).forEach(key => {
                  const placeholder = `{{${key}}}`
                  previewHtml = previewHtml.replace(new RegExp(placeholder, 'g'), sampleData[key])
                })
                
                return previewHtml
              }
              
              return `<div style="padding: 20px; text-align: center;">
                <h2>${template.content?.name || 'Newsletter Template'}</h2>
                <p>Template ID: ${templateId}</p>
                <p>Template source not found</p>
              </div>`
            },
            openHtmlEditor: () => {
              // HTML editor not available in this context
            }
          }
        })
      } else {
        // Fallback to placeholder
        const template = availableTemplates.find(t => t._id === templateId)
        if (template) {
          PagePreview({
            action: "toggle",
            data: {
              getContent: () => `<div style="padding: 20px; text-align: center;">
                <h2>${template.name || 'Newsletter Template'}</h2>
                <p>Template ID: ${templateId}</p>
                <p>Template content not available for preview</p>
              </div>`,
              openHtmlEditor: () => {
                // HTML editor not available in this context
              }
            }
          })
        }
      }
    } catch (error) {
      console.error('Failed to preview template:', error)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-start justify-between mb-2">
          <Typography 
            component="h1"
            variant="admin_h4"
          >
            Email Templates
          </Typography>

          <Button
            variant="admin_secondary"
            onClick={addTemplate}
            disabled={formData.templates.length >= availableTemplates.length}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Template
          </Button>
        </div>

        <Typography variant="admin_txt1_dim">
          Select one or more email templates for this release. Multiple templates enable A/B testing.
        </Typography>
      </div>

      {loadingTemplates ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 rounded-full border-b-2 border-admin_primary animate-spin" />

          <Typography 
            variant="admin_body_dim" 
            className="ml-3"
          >
            Loading templates...
          </Typography>
        </div>
      ) : availableTemplates.length === 0 ? (
        <div className="text-center bg-admin_canvas rounded-lg border border-admin_bordered py-8">
          <Typography variant="admin_body_dim">
            No email templates found. Please create newsletter templates first.
          </Typography>
        </div>
      ) : (
        <>
          {/* Template Table */}
          {formData.templates.length > 0 && (
            <div className="bg-admin_paper rounded-lg border border-admin_bordered overflow-hidden">
              <table className="w-full">
                <thead className="bg-admin_canvas border-b border-admin_bordered">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-admin_text">
                      Template <span className="text-admin_error">*</span>
                    </th>

                    <th className="text-left px-4 py-3 text-sm font-medium text-admin_text w-32">
                      Percentage
                    </th>

                    <th className="text-center px-4 py-3 text-sm font-medium text-admin_text w-24">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {formData.templates.map((template, index) => (
                  <tr key={index} className="border-b border-admin_bordered last:border-b-0">
                    <td className="px-4 py-3">
                      <DropdownSelect
                        variant="admin"
                        searchEnabled
                        fixedWidth="100%"
                        showCheckbox={false}
                        showSelectionTick={true}
                        value={getTemplateOptions(template.templateId).find(opt => opt.value === template.templateId) || null}
                        onChange={(selectedOption) => {
                          // Handle both object and string formats
                          let templateId = '';
                          if (typeof selectedOption === 'string') {
                            templateId = selectedOption;
                          } else if (selectedOption && selectedOption.value) {
                            templateId = selectedOption.value;
                          } else if (Array.isArray(selectedOption) && selectedOption.length > 0) {
                            templateId = selectedOption[0].value || selectedOption[0];
                          }
                          updateTemplate(index, "templateId", templateId);
                        }}
                        options={getTemplateOptions(template.templateId)}
                        placeholder="Select a template..."
                        error={errors[`template_${index}_id`]}
                        required
                      />
                    </td>
                    
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <input
                          type="number"
                          value={template.percentage}
                          onChange={(e) => updateTemplate(index, "percentage", parseInt(e.target.value) || 1)}
                          min={1}
                          max={100}
                          className={`w-20 text-admin_text text-center bg-admin_paper rounded-lg border px-3 py-2 focus:outline-none focus:ring-1 focus:ring-admin_primary focus:border-transparent ${errors[`template_${index}_percentage`] ? "border-admin_error border-2" : "border-admin_bordered"}`}
                        />
                        <span className="ml-1 text-admin_text_secondary">%</span>
                      </div>

                      {errors[`template_${index}_percentage`] && (
                        <Typography 
                          variant="admin_txt2" 
                          className="text-admin_error"
                        >
                          {errors[`template_${index}_percentage`]}
                        </Typography>
                      )}
                    </td>
                    
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center space-x-2">
                        {template.templateId && (
                          <Button
                            variant="admin_icon"
                            onClick={() => handleTemplatePreview(template.templateId)}
                            title="Preview Template"
                            className="text-admin_text_secondary"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </Button>
                        )}
                        
                        <Button
                          variant="admin_icon"
                          onClick={() => removeTemplate(index)}
                          title="Remove Template"
                          className="text-admin_text_secondary hover:text-admin_error"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}

          {/* Template Errors */}
          {errors.templates && (
            <Typography 
              variant="admin_txt2" 
              className="text-admin_error"
            >
              {errors.templates}
            </Typography>
          )}
        </>
      )}

      {/* Template Variables Modal */}
      <TemplateVariablesModal
        isOpen={showVariablesModal}
        onClose={() => {
          setShowVariablesModal(false)
          setSelectedTemplateForVariables(null)
        }}
        templateId={selectedTemplateForVariables?.templateId}
        templateName={selectedTemplateForVariables?.templateName}
        releaseId={releaseId}
        onSave={handleVariablesSaved}
      />
    </div>
  )
}

export default TemplatesStep